import { ApplicationRef, EnvironmentInjector, Injectable, createComponent } from '@angular/core';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { environment } from '../../../environments/environment';
import { DummyRewardedPlayerComponent } from './dummy-rewarded-player.component';
import {
    RewardedAdProvider,
    RewardedAdResult,
} from './rewarded-ad.types';

/**
 * Dev stand-in for the real AppLixir provider.
 *
 * Mirrors the real SDK's presentation: creates its own throwaway anchor div on
 * document.body and renders the fake player as a fixed fullscreen overlay
 * (backdrop + centered panel) — exactly how AppLixir v6 presents itself. No
 * external scripts, no network. The swap dummy/real is a single provider line
 * in app.module behind the same RewardedAdProvider token. See §2a / §13.
 */
@Injectable()
export class DummyRewardedAdProvider extends RewardedAdProvider {
    constructor(
        private appRef: ApplicationRef,
        private environmentInjector: EnvironmentInjector,
        private logger: LoggerService,
    ) {
        super();
    }

    showRewardedAd(): Promise<RewardedAdResult> {
        return new Promise<RewardedAdResult>((resolve) => {
            const anchor = document.createElement('div');
            document.body.appendChild(anchor);

            const durationSeconds = Math.max(1, Math.round((environment.rewardedAdDummyDurationMs ?? 5000) / 1000));

            // Render the player template into the anchor.
            const componentRef = createComponent(DummyRewardedPlayerComponent, {
                environmentInjector: this.environmentInjector,
                hostElement: anchor,
            });
            componentRef.setInput('durationSeconds', durationSeconds);

            let settled = false;
            const subscription = componentRef.instance.finished.subscribe((status) => {
                if (settled) {
                    return;
                }
                settled = true;
                this.logger.log('DummyRewardedAdProvider: settle', status);
                subscription.unsubscribe();
                this.appRef.detachView(componentRef.hostView);
                componentRef.destroy();
                anchor.remove();
                resolve({ status });
            });

            this.appRef.attachView(componentRef.hostView); // attach so the player participates in change detection
        });
    }
}
